import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

import type { ConversationCampaign, IQuestion } from '@feathr/blackbox';
import { CampaignClass } from '@feathr/blackbox';
import { ActionBar } from '@feathr/components';
import { useStore } from '@feathr/extender/state';

import SaveCampaignButton from '../SaveCampaignButton';
import ConversationConfig from './ConversationConfig';
import ConversationPreview from './ConversationPreview';
import ConversationStats from './ConversationStats';
import QuestionConfig from './QuestionConfig';

import { config, content, header, headerText } from './ConversationDetails.css';

function ConversationDetails(): JSX.Element | null {
  const { t } = useTranslation();
  const { campaignId } = useParams<{ campaignId: string }>();
  const { Campaigns } = useStore();

  const campaign = Campaigns.get(campaignId, {
    _cls: CampaignClass.Conversation,
  }) as ConversationCampaign;
  if (campaign.isPending) {
    return null;
  }

  const questions: IQuestion[] = campaign.get('questions');
  const question = questions[0];
  const previewKey = `${campaign.get('theme').palette.primary1Color}${
    campaign.get('theme').palette.accent1Color
  }${campaign.get('logo')}`;

  function handlePreSave(): void {
    const trimText = (text?: string): string => text?.trim() ?? '';

    campaign.set({
      name: trimText(campaign.get('name')),
      url: trimText(campaign.get('url')),
    });

    question.text = trimText(question.text);
    question.response = trimText(question.response);
    if (question.answers) {
      question.answers = question.answers
        .map((answer) => ({ ...answer, text: trimText(answer.text) }))
        .filter((answer) => answer.text !== '');
    }
  }

  function handlePostSave(): void {
    campaign.set({ is_enabled: campaign.isPublished });
    campaign.save();
  }

  const actions = (
    <>
      {!campaign.isPublished && (
        <SaveCampaignButton
          campaign={campaign}
          childModels={[]}
          key={'publish'}
          name={'publish'}
          postSave={handlePostSave}
          preSave={handlePreSave}
          shouldChangeState={true}
          showIcon={true}
          type={'success'}
        />
      )}
      <SaveCampaignButton
        campaign={campaign}
        childModels={[]}
        key={'save'}
        name={'save_changes'}
        preSave={handlePreSave}
        showIcon={true}
        type={'secondary'}
      />
    </>
  );

  return (
    <>
      <div className={header}>
        <h3 className={headerText}>{t('Configuration')}</h3>
        <ActionBar right={actions} usePortal={true} />
      </div>
      <div className={content}>
        <div className={config}>
          <Trans t={t}>
            <p>
              Use Feathr conversations to engage with your users. When enabled, it will initialize
              on pages that have the Feathr super pixel already installed. It will ask the question
              you have configured below. Each answer has its own group for you to track your
              responses!
            </p>
          </Trans>
          <ConversationConfig conversationCampaign={campaign} />
          <QuestionConfig campaign={campaign} question={question} />
        </div>
        <ConversationPreview campaign={campaign} key={previewKey} />
      </div>

      <ConversationStats campaign={campaign} />
    </>
  );
}

export default observer(ConversationDetails);
